// NOTICE: KAPTION-INTEGRATION
// Composable that encapsulates the Campaign Reporting export flow:
//   1. submit an export request (aggregate / single-campaign events / general events)
//   2. if the backend returns a completed export (sync), surface the download URL
//   3. if the backend returns a pending/processing export (202, async), POLL the
//      status endpoint until it completes or fails (with a max timeout)
//   4. expose reactive { phase, progress, downloadUrl, error, isExporting } and
//      helpers to trigger the browser download and reset state.
//
// phase ∈ 'idle' | 'submitting' | 'processing' | 'done' | 'error'
import { ref, computed } from 'vue';
import CampaignsAPI from 'dashboard/api/campaigns';

const POLL_INTERVAL_MS = 1500;
const MAX_POLL_MS = 5 * 60 * 1000; // ~5 minutes

// Convert the aggregate Reports dashboard filter object
// `{ type, granularity, inboxId, campaignIds, days, includeArchived }`
// into the backend `filters` shape
// `{ start_date, end_date, granularity, inbox_id, campaign_ids, campaign_type, include_archived }`.
// Mirrors CampaignReportsPage.vue buildOptions date logic.
export const buildAggregateFilters = (dashboardFilters = {}) => {
  const { type, granularity, inboxId, campaignIds, days, includeArchived } =
    dashboardFilters;

  const isAllTime = days === 'all' || days === undefined || days === null;
  const numDays = Number(days || 7);
  const end = new Date();
  const start = new Date(end.getTime() - numDays * 24 * 60 * 60 * 1000);
  const toIsoDate = date => date.toISOString().slice(0, 10);

  // 'campaigns' → 'one_off'; 'all'/undefined type → omit.
  let campaignType;
  if (type === 'campaigns') {
    campaignType = 'one_off';
  } else if (type && type !== 'all') {
    campaignType = type;
  }

  const filters = {
    granularity,
    campaign_type: campaignType,
    inbox_id: inboxId === 'all' ? undefined : inboxId,
    campaign_ids: campaignIds && campaignIds.length ? campaignIds : undefined,
    start_date: isAllTime ? undefined : toIsoDate(start),
    end_date: isAllTime ? undefined : toIsoDate(end),
    // Default ON; only sent as false when the user unticks "Include archived".
    include_archived: includeArchived === false ? false : undefined,
  };

  // Strip undefined keys for a clean payload.
  Object.keys(filters).forEach(key => {
    if (filters[key] === undefined) delete filters[key];
  });

  return filters;
};

// Single anchor-click download helper (object-URL-free; the backend gives us a URL).
export const triggerDownload = (url, filename) => {
  if (!url) return;
  const link = document.createElement('a');
  link.href = url;
  if (filename) link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const sleep = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

export function useCampaignExport() {
  const phase = ref('idle'); // idle | submitting | processing | done | error
  const progress = ref(0);
  const downloadUrl = ref(null);
  const filename = ref(null);
  const error = ref(null);
  // Holds a structured error for the cap-exceeded case so callers can render the
  // dedicated message. e.g. { code: 'ROW_LIMIT_EXCEEDED', limit, estimated_rows }
  const errorDetail = ref(null);

  const isExporting = computed(
    () => phase.value === 'submitting' || phase.value === 'processing'
  );

  const reset = () => {
    phase.value = 'idle';
    progress.value = 0;
    downloadUrl.value = null;
    filename.value = null;
    error.value = null;
    errorDetail.value = null;
  };

  // Normalise a 422 ROW_LIMIT_EXCEEDED (and other) errors.
  const handleError = err => {
    const data = err?.response?.data;
    if (data?.error === 'ROW_LIMIT_EXCEEDED') {
      errorDetail.value = {
        code: 'ROW_LIMIT_EXCEEDED',
        limit: data.limit,
        estimatedRows: data.estimated_rows,
        message: data.message,
      };
      error.value = data.message || 'ROW_LIMIT_EXCEEDED';
    } else {
      errorDetail.value = null;
      error.value = data?.message || err?.message || 'EXPORT_FAILED';
    }
    phase.value = 'error';
  };

  // Apply an export payload (the `export` object from the API) to local state.
  const applyExport = exportObj => {
    if (!exportObj) return false;
    if (typeof exportObj.progress === 'number')
      progress.value = exportObj.progress;
    if (exportObj.filename) filename.value = exportObj.filename;

    if (exportObj.status === 'completed') {
      progress.value = 100;
      downloadUrl.value = exportObj.download_url || null;
      phase.value = 'done';
      return true;
    }
    if (exportObj.status === 'failed') {
      error.value = exportObj.error_message || 'EXPORT_FAILED';
      phase.value = 'error';
      return true;
    }
    // pending | processing
    phase.value = 'processing';
    return false;
  };

  // Poll the status endpoint until terminal state or timeout.
  const poll = async exportId => {
    const deadline = Date.now() + MAX_POLL_MS;
    while (Date.now() < deadline) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(POLL_INTERVAL_MS);
      try {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await CampaignsAPI.getExportStatus(exportId);
        const done = applyExport(data?.export);
        if (done) return;
      } catch (err) {
        handleError(err);
        return;
      }
    }
    // Timed out
    error.value = 'EXPORT_TIMEOUT';
    phase.value = 'error';
  };

  // Shared submit pipeline: run `requestFn`, apply the response, poll if async.
  const submit = async requestFn => {
    reset();
    phase.value = 'submitting';
    try {
      const { data } = await requestFn();
      const exportObj = data?.export;
      const finished = applyExport(exportObj);
      if (!finished && exportObj?.id) {
        await poll(exportObj.id);
      } else if (!finished) {
        // No id to poll and not completed — treat as failure.
        error.value = 'EXPORT_FAILED';
        phase.value = 'error';
      }
    } catch (err) {
      handleError(err);
    }
  };

  // Path 2 — aggregate analytics export.
  const startAggregateExport = ({ format, groupBy, filters }) =>
    submit(() => CampaignsAPI.exportAggregate({ format, groupBy, filters }));

  // Path 3 — single-campaign delivery events export.
  const startCampaignEventsExport = (
    campaignId,
    { format, status, columns, maxRows }
  ) =>
    submit(() =>
      CampaignsAPI.exportCampaignEvents(campaignId, {
        format,
        status,
        columns,
        maxRows,
      })
    );

  // Path 3 — general/multi-campaign delivery events export.
  const startGeneralEventsExport = ({
    format,
    status,
    columns,
    maxRows,
    filters,
  }) =>
    submit(() =>
      CampaignsAPI.exportGeneralEvents({
        format,
        status,
        columns,
        maxRows,
        filters,
      })
    );

  const download = () => triggerDownload(downloadUrl.value, filename.value);

  return {
    // reactive state
    phase,
    progress,
    downloadUrl,
    filename,
    error,
    errorDetail,
    isExporting,
    // actions
    startAggregateExport,
    startCampaignEventsExport,
    startGeneralEventsExport,
    download,
    reset,
    // helpers
    triggerDownload,
    buildAggregateFilters,
  };
}

export default useCampaignExport;
